import React from 'react';
import { Box, Typography } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import { StudioFlowColors } from '../studioFlowTheme';

interface FlowLoadingOverlayProps {
  message: string;
  fullScreen?: boolean;
}

const pulse = keyframes`
  0%, 80%, 100% {
    opacity: 0.28;
    transform: translateY(0);
  }
  40% {
    opacity: 1;
    transform: translateY(-3px);
  }
`;

export const FlowLoadingOverlay: React.FC<FlowLoadingOverlayProps> = ({
  message,
  fullScreen = false,
}) => {
  const content = (
    <>
      <Typography
        sx={{
          color: StudioFlowColors.subtitle,
          fontFamily: StudioFlowColors.bodyFontFamily,
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: '-0.13px',
        }}
      >
        {message}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {[0, 1, 2].map((index) => (
          <Box
            key={index}
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: StudioFlowColors.primary,
              animation: `${pulse} 1.1s ease-in-out infinite`,
              animationDelay: `${index * 0.14}s`,
            }}
          />
        ))}
      </Box>
    </>
  );

  if (fullScreen) {
    return (
      <Box
        role="status"
        aria-live="polite"
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 1400,
          bgcolor: 'rgba(255, 255, 255, 0.86)',
          backdropFilter: 'blur(3px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 3,
        }}
      >
        <Box
          sx={{
            minWidth: 260,
            borderRadius: '24px',
            bgcolor: StudioFlowColors.background,
            boxShadow: '0 18px 52px rgba(1, 11, 46, 0.14)',
            px: 4,
            py: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.25,
          }}
        >
          {content}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.25,
        minHeight: 24,
      }}
    >
      {content}
    </Box>
  );
};
